/**
 * MyDealsPage
 *
 * Displays the customer's deals with stage badges, values, and expected close dates.
 * Supports pagination for customers with many deals.
 * Read-only — customers can view but not modify deals.
 *
 * Requirements: Task 6 - My Deals Page
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardContent,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@bloomcode/ui';
import { Handshake, RefreshCw, ChevronLeft, ChevronRight } from '@bloomcode/icons';

import { useMyDeals } from '@/hooks/useMyDeals';
import { DealCard } from '@/components/deals';

/**
 * Empty state when no deals exist
 */
function EmptyState() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-16 text-center">
        <Handshake className="h-12 w-12 text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No Deals Yet</h3>
        <p className="text-muted-foreground max-w-sm">
          When your account team shares deals or quotes with you, they will appear here.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Pagination controls
 */
function PaginationControls({
  page,
  totalPages,
  hasNext,
  hasPrev,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between mt-4">
      <p className="text-sm text-muted-foreground">
        Page {page} of {totalPages}
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrev}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNext}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function MyDealsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { deals, pagination, totalCount, isLoading, error, refetch } = useMyDeals({
    page,
    limit: 10,
  });

  const handleDealClick = (id: string) => {
    navigate(`/deals/${id}`);
  };

  // Loading state
  if (isLoading && deals.length === 0) {
    return (
      <>
        <Helmet>
          <title>My Deals | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-heading font-bold">My Deals</h1>
            <p className="text-muted-foreground">
              View deals and quotes shared with you by your account team.
            </p>
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="animate-pulse space-y-3">
                    <div className="flex justify-between">
                      <div className="h-5 w-48 rounded bg-muted" />
                      <div className="h-5 w-20 rounded bg-muted" />
                    </div>
                    <div className="h-4 w-32 rounded bg-muted" />
                    <div className="flex gap-4">
                      <div className="h-4 w-24 rounded bg-muted" />
                      <div className="h-4 w-16 rounded bg-muted" />
                      <div className="h-4 w-28 rounded bg-muted" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error && deals.length === 0) {
    return (
      <>
        <Helmet>
          <title>My Deals | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-heading font-bold">My Deals</h1>
          </div>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-destructive mb-2">Failed to load deals</p>
              <p className="text-sm text-muted-foreground mb-4">{error.message}</p>
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Deals | Bloomcode CRM Portal</title>
      </Helmet>

      <div className="container mx-auto p-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>My Deals</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Page Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold">My Deals</h1>
            <p className="text-muted-foreground">
              {totalCount > 0
                ? `${totalCount} deal${totalCount !== 1 ? 's' : ''} shared with you`
                : 'View deals and quotes shared with you by your account team.'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
            aria-label="Refresh deals"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Deal List or Empty State */}
        {deals.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="space-y-3" role="list" aria-label="Deals list">
              {deals.map((deal) => (
                <div key={deal.id} role="listitem">
                  <DealCard deal={deal} onClick={handleDealClick} />
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <PaginationControls
                page={pagination.page}
                totalPages={pagination.pages}
                hasNext={pagination.hasNext}
                hasPrev={pagination.hasPrev}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
